/**
 * 고장 정의 격차 — 아령(dumbbell) 차트 (DESIGN §8.6.2).
 *
 * **이 화면에서 가장 중요한 그래프다.** 같은 데이터에 정의만 바꾸면 가동률이
 * 99.7% 에서 65% 로 내려간다. 어느 쪽도 틀리지 않았다 — 무엇을 "고장"으로
 * 셀지 정하지 않은 채 가동률을 말하는 것이 틀린 것이다.
 *
 * 두 값의 *차이*가 주인공이므로 막대 두 개를 나란히 놓지 않는다. 아령은 차이를
 * 선분의 길이 하나로 보여주고, 정렬하면 "누가 가장 많이 움직이는가"가 곧 순서가
 * 된다. 두 끝점 색은 좋고 나쁨이 아니라 **정체성**이므로 상태색을 쓰지 않는다.
 */

import { useQueries } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, niceBand } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, markSize, pct, pctAxis, pp } from '@/lib/fmt'
import { useEntityLabel } from '@/lib/labels'
import {
  MODE_LABEL,
  RANK_ENTITIES,
  type Envelope,
  type RankEntity,
  type RankingData,
} from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

const TOP_N = 12
const POLES = ['strict', 'user_facing'] as const

interface Row {
  id: string
  strict: number
  userFacing: number
  gap: number
  n: number
}

export function ModeGapDumbbell({ entity }: { entity: RankEntity }) {
  const filters = useFilters()
  const labelOf = useEntityLabel()

  const results = useQueries({
    queries: POLES.map((mode) => ({
      queryKey: ['metrics/ranking', entity, mode, filterKey(filters)],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchEnvelope<RankingData>(
          '/metrics/ranking',
          { ...toQueryFilters(filters), mode },
          { entity },
          signal,
        ),
      staleTime: 5 * 60 * 1000,
    })),
  })

  const isLoading = results.some((r) => r.isLoading)
  const error = (results.find((r) => r.error)?.error ?? null) as Error | null
  const strictEnv = results[0]?.data as Envelope<RankingData> | undefined
  const facingEnv = results[1]?.data as Envelope<RankingData> | undefined

  const rows = useMemo<Row[]>(() => {
    if (!strictEnv || !facingEnv) return []
    const facing = new Map(facingEnv.data.ranking.map((r) => [r.entity_id, r]))
    const out: Row[] = []
    for (const s of strictEnv.data.ranking) {
      const u = facing.get(s.entity_id)
      // 한쪽 모드에서만 관측된 대상은 격차를 말할 수 없다 — 0 으로 채우지 않는다
      if (!u || s.availability == null || u.availability == null) continue
      out.push({
        id: s.entity_id,
        strict: s.availability,
        userFacing: u.availability,
        gap: s.availability - u.availability,
        n: s.n_chargers,
      })
    }
    return out.sort((a, b) => b.gap - a.gap)
  }, [strictEnv, facingEnv])

  const shown = rows.slice(0, TOP_N)
  const axisLabel = RANK_ENTITIES.find((e) => e.key === entity)?.label ?? '대상'
  const dropped = rows.length - shown.length
  const widest = rows[0] ?? null

  const option = useMemo<EChartsOption>(() => {
    const items = [...shown].reverse()
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const c2 = cssVar('--c-cat-2', '#a35200')
    const ink3 = cssVar('--c-ink-3', '#6e6e73')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const canvas = cssVar('--c-canvas', '#ffffff')

    const band = niceBand(Math.max(0, Math.min(...items.map((r) => r.userFacing), 1) - 0.04), 1)

    return {
      grid: { left: 108, right: 64, top: 8, bottom: 26 },
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const r = items[(p as { dataIndex: number }).dataIndex]
          if (!r) return ''
          return [
            `<b>${labelOf(entity, r.id)}</b> · ${int(r.n)}기`,
            `${MODE_LABEL.strict} ${pct(r.strict)}`,
            `${MODE_LABEL.user_facing} ${pct(r.userFacing)}`,
            `격차 <b>${pp(-r.gap)}</b>`,
          ].join('<br/>')
        },
      },
      xAxis: {
        type: 'value',
        min: band.min,
        max: band.max,
        interval: band.interval,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: pctAxis(band.interval * 5) },
      },
      yAxis: {
        type: 'category',
        data: items.map((r) => labelOf(entity, r.id)),
        ...axisStyle(ink3, hairline),
        splitLine: { show: false },
        axisLabel: {
          fontSize: 11,
          color: cssVar('--c-ink-2', '#4a4a4f'),
          width: 100,
          overflow: 'truncate',
        },
      },
      series: [
        {
          type: 'custom',
          // [이용자관점, 설비고장만, 카테고리인덱스, 충전기수]
          data: items.map((r, i) => [r.userFacing, r.strict, i, r.n]),
          encode: { x: [0, 1], y: 2 },
          renderItem: (_params, api) => {
            const cat = api.value(2) as number
            const [xU, y] = api.coord([api.value(0) as number, cat])
            const [xS] = api.coord([api.value(1) as number, cat])
            // 아령의 굵기 = √충전기수. 1기짜리와 3만기짜리를 같은 무게로 보이면 안 된다
            const r = markSize(api.value(3) as number, 3.5, 9, 0.05)
            return {
              type: 'group',
              children: [
                {
                  type: 'line',
                  shape: { x1: xU, y1: y, x2: xS, y2: y },
                  style: { stroke: ink3, lineWidth: 2, opacity: 0.45 },
                },
                {
                  type: 'circle',
                  shape: { cx: xU, cy: y, r },
                  style: { fill: c2, stroke: canvas, lineWidth: 2 },
                },
                {
                  type: 'circle',
                  shape: { cx: xS, cy: y, r },
                  style: { fill: c1, stroke: canvas, lineWidth: 2 },
                },
              ],
            }
          },
        },
        // 격차 수치를 각 행 오른쪽 끝에 직접 붙인다 — 툴팁 없이도 읽힌다
        {
          type: 'scatter',
          symbolSize: 0,
          data: items.map((_r, i) => [band.max, i]),
          label: {
            show: true,
            position: 'right',
            distance: 8,
            fontSize: 11,
            color: cssVar('--c-ink-2', '#4a4a4f'),
            formatter: (p: { dataIndex: number }) => {
              const gap = items[p.dataIndex]?.gap
              return gap == null ? '' : pp(-gap, 1)
            },
          },
          silent: true,
        },
      ],
    }
  }, [shown, entity, labelOf])

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">고장 정의별 가동률과 격차</caption>
        <thead>
          <tr>
            <th scope="col">대상</th>
            <th scope="col" className="ta-r">
              충전기
            </th>
            <th scope="col" className="ta-r">
              {MODE_LABEL.strict}
            </th>
            <th scope="col" className="ta-r">
              {MODE_LABEL.user_facing}
            </th>
            <th scope="col" className="ta-r">
              격차
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{labelOf(entity, r.id)}</td>
              <td className="num ta-r">{int(r.n)}</td>
              <td className="num ta-r">{pct(r.strict)}</td>
              <td className="num ta-r">{pct(r.userFacing)}</td>
              <td className="num ta-r">{pp(-r.gap)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="고장 정의 격차"
      question="'설비 고장만' 과 '이용자 관점' 사이의 격차 — 클수록 이용 제한(limitYn=Y)에 크게 노출된 대상"
      meta={strictEnv?.meta}
      headline={
        widest ? (
          <Headline value={pp(-widest.gap, 1)} unit={`최대 격차 · ${labelOf(entity, widest.id)}`} />
        ) : undefined
      }
      reading={`같은 관측 데이터에 "무엇을 고장으로 셀지"만 바꿔 두 번 계산한 결과를 한 줄로 이었습니다. 선이 길수록 정의에 따라 결론이 크게 달라지는 ${axisLabel}입니다.`}
      encoding={[
        {
          role: '가로축',
          what: '가동률 (%)',
          detail:
            '오른쪽으로 갈수록 가동률이 높습니다. 한 행의 두 점은 같은 대상·같은 관측이며, 달라지는 것은 어떤 상태 코드를 고장으로 셀지뿐입니다.',
        },
        {
          role: '세로축',
          what: `${axisLabel} · 격차 큰 순`,
          detail: '위에 있을수록 정의를 바꿀 때 더 크게 움직입니다.',
        },
        {
          role: '파란 점',
          what: `${MODE_LABEL.strict} 기준 가동률`,
          detail: '운영중지·점검중만 고장으로 셉니다. 사업자 연계 문제를 고장으로 세지 않아 값이 가장 높습니다.',
        },
        {
          role: '주황 점',
          what: `${MODE_LABEL.user_facing} 기준 가동률`,
          detail: '통신이상과 이용 제한(limitYn=Y)까지 고장으로 셉니다. 이용자가 실제로 못 쓰는 시간에 가깝습니다.',
        },
        {
          role: '선 길이',
          what: '두 정의의 격차 (포인트)',
          detail: '오른쪽 끝에 숫자로도 적었습니다. 부호는 이용자 관점이 더 낮다는 뜻입니다.',
        },
        {
          role: '점 크기',
          what: '충전기 수 (지름 ∝ √n)',
          detail: '가느다란 아령은 몇 대 안 되는 대상이라 격차 자체가 흔들릴 수 있습니다.',
        },
      ]}
      legend={[
        {
          label: `${MODE_LABEL.strict} (설비 고장만 셈)`,
          color: cssVar('--c-cat-1', '#0066cc'),
          pattern: 'dot',
        },
        {
          label: `${MODE_LABEL.user_facing} (이용 제한 포함)`,
          color: cssVar('--c-cat-2', '#a35200'),
          pattern: 'dot',
        },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={rows.length === 0}
      onRetry={() => results.forEach((r) => void r.refetch())}
      table={table}
      notes={[
        {
          label: '어느 쪽도 틀리지 않았다.',
          text: '틀린 것은 무엇을 고장으로 셀지 정하지 않은 채 "가동률 99%"라고 말하는 일입니다. 이 화면은 그 선택이 결론을 얼마나 바꾸는지 보여줍니다.',
        },
        {
          label: '실측 55%가 이용 제한 상태였다.',
          text: '전체의 절반이 넘게 limitYn=Y 였고, 이것이 이용자 관점 가동률을 크게 끌어내립니다 (§15.3).',
        },
        {
          label: '기본 모드는 두 끝점 사이에 있다.',
          text: '상단 "고장 정의" 토글의 통신 포함(standard)은 파란 점과 주황 점 사이 어딘가에 놓입니다.',
        },
        {
          label: '두 모드 모두에서 관측된 대상만 그린다.',
          text: '한쪽에만 있는 대상은 격차를 계산할 수 없어 0 으로 채우지 않고 빼둡니다.',
        },
        dropped > 0
          ? { label: `격차 상위 ${TOP_N}개만 그린다.`, text: `나머지 ${dropped}개는 표 보기에 있습니다.` }
          : '',
      ]}

    >
      <EChart
        option={option}
        height={Math.max(180, shown.length * 28 + 40)}
        ariaLabel={`고장 정의별 가동률 격차. ${shown.length}개 대상. 최대 격차 ${
          widest ? `${labelOf(entity, widest.id)} ${pp(-widest.gap, 1)}` : '없음'
        }. 정확한 값은 표 보기에 있습니다.`}
      />
    </ChartFrame>
  )
}
